import type { FC, ReactNode } from 'react'
import type { Hill } from '@/lib/contours'

import { useEffect, useMemo, useState } from 'react'
import { buildContours } from '@/lib/contours'

interface ContourPathsProps {
    hills: Hill[]
    strokeWidth?: number
    className?: string
}

interface ContourFieldProps extends ContourPathsProps {
    viewBox: string
    preserveAspectRatio?: string
}

/**
 * Topographic contour lines. They are drawn after mount only, because the
 * organic curves rely on trigonometry that can differ slightly between engines.
 */
export const ContourPaths: FC<ContourPathsProps> = ({ hills, strokeWidth = 1, className }): ReactNode => {
    const [ready, setReady] = useState(false)

    useEffect(() => {
        setReady(true)
    }, [])

    const paths = useMemo(() => (ready ? buildContours(hills) : []), [ready, hills])

    return (
        <g className={className} fill='none' stroke='currentColor' aria-hidden='true'>
            {paths.map((d, index) => (
                <path key={index} d={d} strokeWidth={strokeWidth} vectorEffect='non-scaling-stroke' />
            ))}
        </g>
    )
}

export const ContourField: FC<ContourFieldProps> = ({
    hills,
    viewBox,
    strokeWidth,
    className,
    preserveAspectRatio = 'xMidYMid slice'
}): ReactNode => {
    return (
        <svg
            viewBox={viewBox}
            preserveAspectRatio={preserveAspectRatio}
            className={className}
            aria-hidden='true'
            focusable='false'
        >
            <ContourPaths hills={hills} strokeWidth={strokeWidth} />
        </svg>
    )
}
